import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useAccountToDisplaySelector } from "../../redux/actions/user";
import { tt } from "../../utils/i18n/i18n";
import POSAApi, { type POSADeliveryWithBill } from "./api";
import { finishedFilter, ongoingFilter } from "./utils";
import { POSADeliveriesTable } from "./POSADeliveriesTable";
import ArtworkModal, { type ArtworkModalData } from "./ArtworkModal";
import { type POSADeliveryJavaDTO } from "@tootsweet/model/lottery/POSADelivery";
import { FullScreenLoader } from "../../components/Loaders/FullScreenLoader/FullScreenLoader";
import { distributionWithTracking, POSA } from "../../utils/routes/routes";
import { buildRedirectURL } from "../../utils/helpers/buildRedirectUrl";
import KC_Heading2 from "../../designsystem/typography/KC-Heading/KC_Heading2";
import KC_Separator from "../../designsystem/KC_Separator/KC_Separator";
import { KC_Sizes } from "../../designsystem/ds_commons";
import TestsID from "../../utils/TestsID";
import KC_Button, {
  KC_ButtonSize
} from "../../designsystem/KC_Button/KC_Button";
import { faBasketShopping } from "@fortawesome/free-solid-svg-icons";
import { buildPathWithTrackingId } from "../../utils/routing/routingUtils";
import { ManagedAccountDropdown } from "../../components/ManagedAccountDropdown/ManagedAccountDropdown";

export default function POSAManager() {
  const { accountToDisplay } = useAccountToDisplaySelector();
  const history = useHistory();

  const [deliveries, setDeliveries] = useState<POSADeliveryJavaDTO[] | null>(
    null
  );
  const [loading, setIsLoading] = useState(false);
  const [modalData, setModalData] = useState<ArtworkModalData>({
    show: false,
    doNotValidate: false,
    delivery: undefined,
    validate: false
  });
  const paramz = useParams();

  const aggregate = accountToDisplay?.email?.toLowerCase() === "all";

  const fetch = async () => {
    if (!accountToDisplay?._id) {
      alert(tt("error"));
      return;
    }

    try {
      setIsLoading(true);
      const deliveries = await POSAApi.getPOSAList(
        // do not change this, because legacy backend request
        // need the _id field, not trackingID
        accountToDisplay?._id,
        aggregate
      );
      setDeliveries(deliveries);
    } catch (err) {
      console.error(err);
      alert(tt("error"));
    } finally {
      setIsLoading(false);
    }
  };

  const showModal = (delivery: POSADeliveryWithBill) => {
    setModalData({
      show: true,
      fileId: delivery.googleDriveFileId,
      doNotValidate: false,
      delivery,
      validate: false
    });
  };

  const hideModal = () => {
    setModalData({
      show: false,
      doNotValidate: false,
      delivery: undefined,
      validate: false,
      fileId: undefined
    });
  };

  const validateArtwork = async () => {
    void updateDelivery(true);
  };

  const doNotValidateArtwork = async (comment: string) => {
    void updateDelivery(false, comment);
  };

  const goToFlyersOrder = () => {
    history.push(
      buildPathWithTrackingId(
        distributionWithTracking,
        accountToDisplay?.trackingID
      )
    );
  };

  const updateDelivery = async (validate: boolean, comment?: string) => {
    try {
      if (modalData.delivery == null) {
        alert(tt("api_error"));
        return;
      }
      setModalData({ ...modalData, validate, show: !validate });
      setIsLoading(true);
      const index = deliveries?.findIndex(
        (d) => d.id === modalData.delivery?.id
      );
      if (index === undefined || index === -1) {
        alert(tt("api_error"));
        return;
      }
      if (validate) {
        await POSAApi.validate(modalData.delivery.id);
      } else {
        if (!comment) {
          alert(tt("api_error"));
          return;
        }
        await POSAApi.doNotValidate(modalData.delivery.id, comment);
      }
      window.location.href = buildRedirectURL(POSA, false);
    } catch (err) {
      console.error(err);
      alert(tt("api_error"));
    } finally {
      setIsLoading(false);
    }
  };

  const afterPaymentSuccessful = async (paymentIntentId?: string) => {
    if (modalData.delivery == null || !accountToDisplay?.trackingID) {
      return;
    }
    const delivery = await POSAApi.getOne(
      modalData.delivery.id,
      accountToDisplay?.trackingID
    );
    const index = deliveries?.findIndex((d) => d.id === modalData.delivery?.id);
    if (index === undefined || index === -1) {
      return;
    }
    updateDeliveryAtIndex(delivery, index);
  };

  const updateDeliveryAtIndex = (
    newDelivery: POSADeliveryJavaDTO,
    index: number
  ) => {
    const newDeliveries = [...(deliveries || [])];
    newDeliveries[index] = newDelivery;
    setDeliveries(newDeliveries);
  };

  useEffect(() => {
    void fetch();
  }, [accountToDisplay?.trackingID]);

  useEffect(() => {
    // @ts-expect-error
    if (deliveries != null && paramz.deliveryId) {
      const delivery = deliveries.find(
        // @ts-expect-error
        (delivery) => delivery.id === paramz.deliveryId
      );
      if (delivery != null) {
        showModal(delivery);
      } else {
        alert(tt("api_error"));
      }
    }
    // @ts-expect-error
  }, [deliveries, paramz.deliveryId]);

  if (!accountToDisplay?.email) {
    return null;
  }

  return (
    <div
      id={TestsID.SUPPORTS_PAGE}
      className="d-flex flex-column rounded-box p-4 dashboard-v2"
    >
      <FullScreenLoader loading={loading} />
      <div className="d-flex flex-column mb-4">
        <ManagedAccountDropdown />
      </div>
      <KC_Button
        id="dashboard_btn_posa"
        key="dashboard_btn_posa"
        className="d-none d-sm-flex ml-md-auto"
        extraStyle={{
          width: 200,
          position: "absolute",
          bottom: 16,
          right: 16
        }}
        text={tt("saas_summary_completed_section_action_order_pdf")}
        icon={faBasketShopping}
        showIconOnTheLeft={true}
        isPrimary={true}
        size={KC_ButtonSize.Large}
        onClick={goToFlyersOrder}
      />
      <KC_Heading2 text={tt("posa_ongoing_printing")} />
      <POSADeliveriesTable
        keyPrefix={"ongoing"}
        aggregate={aggregate}
        posaDeliveries={deliveries?.filter(ongoingFilter)}
        showModal={showModal}
        hideCTA={false}
        businessUserId={accountToDisplay.email}
      />
      <KC_Separator size={KC_Sizes.l} />
      <KC_Heading2 text={tt("posa_past_artwork")} />
      <POSADeliveriesTable
        keyPrefix={"done"}
        aggregate={aggregate}
        posaDeliveries={deliveries?.filter(finishedFilter)}
        showModal={showModal}
        hideCTA={true}
        businessUserId={accountToDisplay.email}
      />
      <KC_Separator size={KC_Sizes.l} />
      <ArtworkModal
        hideModal={hideModal}
        modalData={modalData}
        setModalData={setModalData}
        validate={validateArtwork}
        doNotValidate={doNotValidateArtwork}
        setIsLoading={setIsLoading}
        afterPaymentSuccessful={afterPaymentSuccessful}
      />

      <div
        className="d-flex d-sm-none mt-auto position-sticky"
        style={{
          bottom: 0
        }}
      >
        <KC_Button
          id="dashboard_btn_posa"
          key="dashboard_btn_posa"
          className="d-sm-none"
          text={tt("saas_summary_completed_section_action_order_pdf")}
          icon={faBasketShopping}
          showIconOnTheLeft={true}
          isPrimary={true}
          size={KC_ButtonSize.Large}
          onClick={goToFlyersOrder}
        />
      </div>
    </div>
  );
}
